import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Bus,
  Ticket,
  Package,
  ClipboardList,
  Truck,
  UserCog,
  Map,
  MapPin,
  Wallet,
  Receipt,
  ArrowLeftRight,
  Banknote,
  CalendarDays,
  Users,
  LogOut,
  X,
} from 'lucide-react';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}

const GROUPS: NavGroup[] = [
  {
    label: 'Home',
    items: [
      { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/operations/trips', label: 'Trips', icon: Bus },
      { to: '/admin/operations/bookings', label: 'Bookings', icon: Ticket },
      { to: '/admin/operations/parcels', label: 'Parcels', icon: Package },
      { to: '/admin/operations/manifest', label: 'Manifest', icon: ClipboardList },
    ],
  },
  {
    label: 'Fleet',
    items: [
      { to: '/admin/fleet/vehicles', label: 'Vehicles', icon: Truck },
      { to: '/admin/fleet/drivers', label: 'Drivers', icon: UserCog },
      { to: '/admin/fleet/routes', label: 'Routes', icon: Map },
      { to: '/admin/fleet/stops', label: 'Stops', icon: MapPin },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/admin/finance', label: 'Overview', icon: Wallet, end: true },
      { to: '/admin/finance/day-summary', label: 'Day summary', icon: CalendarDays },
      { to: '/admin/finance/expenses', label: 'Expenses', icon: Receipt },
      { to: '/admin/finance/withdrawals', label: 'Withdrawals', icon: Banknote },
      { to: '/admin/finance/transactions', label: 'Transactions', icon: ArrowLeftRight },
      { to: '/admin/finance/parcel-revenue', label: 'Parcel revenue', icon: Package },
    ],
  },
  {
    label: 'People',
    items: [{ to: '/admin/people/users', label: 'Users', icon: Users }],
  },
];

const CLERK_ITEMS = new Set([
  '/admin/operations/bookings',
  '/admin/operations/parcels',
  '/admin/operations/trips',
  '/admin/operations/manifest',
]);

// Items only the admin role may see (transport_manager is excluded).
// Wallet management (Finance overview, withdrawals, transactions) is admin-only;
// Expenses and Parcel revenue remain management-visible.
const ADMIN_ONLY_ITEMS = new Set([
  '/admin/people/users',
  '/admin/finance',
  '/admin/finance/withdrawals',
  '/admin/finance/transactions',
]);

interface Props {
  userName: string;
  roleLabel: string;
  role?: string;
  onLogout: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

const AdminSidebar: React.FC<Props> = ({
  userName,
  roleLabel,
  role,
  onLogout,
  mobileOpen,
  onCloseMobile,
}) => {
  const visibleGroups = GROUPS
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => {
        if (role === 'clerk') return CLERK_ITEMS.has(it.to);
        if (ADMIN_ONLY_ITEMS.has(it.to)) return role === 'admin';
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      {/* Mobile backdrop */}
      <div
        aria-hidden
        onClick={onCloseMobile}
        className={`fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-sm transition-opacity md:hidden ${
          mobileOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      />

      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-brand-900 text-white shadow-2xl transition-transform duration-200',
          'md:sticky md:top-0 md:z-30 md:h-screen md:w-64 md:translate-x-0 md:shadow-none',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Header / brand */}
        <div className="flex items-center justify-between border-b border-brand-800/60 bg-brand-950/40 px-5 py-4">
          <div className="flex items-center gap-3">
            {/* Slanted wordmark, as painted on the shuttles */}
            <p className="-skew-x-6 bg-livery-600 px-2.5 py-1 font-signage text-[1.35rem] font-extrabold italic uppercase leading-none tracking-wide text-white">
              Cruiser
            </p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-200/80">
              Admin
            </p>
          </div>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onCloseMobile}
            className="rounded-md p-1.5 text-brand-100 hover:bg-white/10 md:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User block */}
        <div className="border-b border-brand-800/60 px-5 py-4">
          <p className="truncate text-base font-semibold">
            {userName || 'Signed in'}
          </p>
          {roleLabel && (
            <span className="mt-1.5 inline-flex items-center rounded-full bg-brand-800 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-brand-100">
              {roleLabel}
            </span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {visibleGroups.map((group, groupIdx) => (
            <div key={group.label} className={groupIdx > 0 ? 'mt-6' : ''}>
              <p className="px-3 pb-2 text-[11px] font-bold uppercase tracking-wider text-brand-200/70">
                {group.label}
              </p>
              <div className="flex flex-col gap-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={onCloseMobile}
                      className={({ isActive }) =>
                        [
                          'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300',
                          isActive
                            ? 'bg-brand-700 font-semibold text-white'
                            : 'text-brand-100 hover:bg-white/5 hover:text-white',
                        ].join(' ')
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <span
                              aria-hidden
                              className="absolute -left-3 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-accent-500"
                            />
                          )}
                          <Icon className="h-4 w-4 shrink-0" aria-hidden />
                          <span>{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer / logout */}
        <div className="border-t border-brand-800/60 p-3">
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-brand-100 hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
